/**
 * Desktop Playtests — INVENTORY-DESKTOP §3.7 (lane D4).
 *
 * Every string, state and bucket is SAME as mobile (INVENTORY §4.5): this file
 * re-lays out `playtestsVM` + `ptSyncChip`, it does not re-derive them. The four
 * strings the desktop spells differently live in `store/selectors.d4.ts` (§8),
 * as does `ptWinBorder` — the one field `lib/logic.ts#ptWinBox` never needed on
 * mobile, because mobile drew no box around the window block.
 *
 * LAYOUT
 *   header      `padding:30px 26px 0; gap:14px; align-items:flex-end` — title +
 *               sub, spacer, then ONE right-aligned action row (mobile stacked
 *               them 2×2): sync chip · ★ TRACKED (N) · ⊞ BROWSE STEAM ·
 *               + ADD PLAYTEST.
 *   filter row  `padding:16px 26px 2px; gap:8px` — SearchBox(260) · 4 chips ·
 *               SHOW/HIDE ENDED · a 1×22 `--gt-border-2` divider · FilterBar.
 *   row card    five columns on ONE line, `gap:16px; padding:14px;
 *               align-items:stretch; flex-wrap:wrap`:
 *                 108px window box · 190px art · flex:1/min 260 body ·
 *                 220px actions.
 *               Below ~870px viewport the actions column wraps — the
 *               prototype's own behaviour (§10.3), not a bug.
 *
 * `style-hover` → CSS (§3.11): the card is `d-hov-card`, the sync chip is
 * `d-hov-bd3`. Both classes live in `styles/desktop.css`; the inline value they
 * override has to stay inline, which is why the rules are `!important` there.
 *
 * NO HOVER TILT HERE. §6.4 lists the five tilt call sites and this is not one of
 * them — playtest art is a static 460/215 thumbnail.
 *
 * `now` is re-read every render and the ui slice's 5 s `tick` forces those
 * renders, so `3 DAYS LEFT` / `CHECKED 12S AGO` stay honest with no timer here.
 */

import { useEffect, useRef } from 'react';

import { Avatar } from '../../components/Avatar.tsx';
import { PixelTriangle } from '../../components/PixelTriangle.tsx';
import { PtCover } from '../../components/PtArt.tsx';
import { SearchBox } from '../../components/SearchBox.tsx';
import { useQd } from '../../store/index.ts';
import { ptSyncChip } from '../../store/selectors.3e.ts';
import {
  ptWinBorder,
  PT_DATA_FOOTER_DESKTOP,
  PT_EMPTY_DESKTOP,
  PT_SUB_DESKTOP,
  PT_SYNC_TIP,
} from '../../store/selectors.d4.ts';
import { playtestsVM } from '../../store/selectors.ts';
import FilterBar from '../components/FilterBar.tsx';
import type { CSSProperties } from 'react';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

/** The three secondary actions share one box (prototype lines 428-430). */
const ACTION: CSSProperties = {
  border: '1px solid var(--gt-border-2)',
  padding: '8px 12px',
  fontFamily: MONO,
  fontSize: 11,
  color: 'var(--gt-text-3)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

/** Filter-row chip + the SHOW/HIDE ENDED toggle (prototype lines 435, 437). */
const chip = (bd: string, clr: string, bg: string): CSSProperties => ({
  border: '1px solid ' + bd,
  color: clr,
  background: bg,
  padding: '7px 10px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
});

export default function Playtests(): JSX.Element {
  const s = useQd();
  const now = Date.now();
  const v = playtestsVM(s, now);
  const sync = ptSyncChip(s, now);

  /** The asset fetch the art ladder needs — see the phone screen's copy. */
  const ptAppids = v.appids;
  const appidRef = useRef<number[]>(ptAppids);
  appidRef.current = ptAppids;
  const appidKey = ptAppids.join(',');
  useEffect(() => {
    if (appidRef.current.length) useQd.getState().queueAssets(appidRef.current);
  }, [appidKey]);

  return (
    <div data-screen-label="Playtests" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* header — title/sub on the left, all four actions on one right-aligned row */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, padding: '30px 26px 0', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontFamily: PIXEL, fontSize: 13, color: 'var(--gt-text)' }}>PLAYTESTS</div>
          <div style={{ fontFamily: MONO, fontSize: '10.5px', letterSpacing: '.05em', color: 'var(--gt-muted)' }}>
            {PT_SUB_DESKTOP}
          </div>
        </div>
        <div style={{ flex: 1 }} />

        <div
          onClick={s.openPtSync}
          className="d-hov-bd3"
          data-tip={PT_SYNC_TIP}
          data-tip-pos="r"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            border: '1px solid ' + sync.bd,
            padding: '8px 12px',
            fontFamily: MONO,
            fontSize: 10,
            color: sync.clr,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          <span style={{ width: 7, height: 7, background: sync.dot, flex: 'none' }} />
          <span>{sync.lbl}</span>
        </div>
        <div onClick={s.openPtTracked} style={ACTION}>
          ★ TRACKED ({v.trackedN})
        </div>
        <div onClick={s.openPtBrowse} style={ACTION}>
          ⊞ BROWSE STEAM
        </div>
        <div onClick={s.openPtAdd} style={{ ...ACTION, border: '1px solid var(--gt-text)', color: 'var(--gt-text)' }}>
          + ADD PLAYTEST
        </div>
      </div>

      {/* search · chips · ended toggle · divider · shared filter rail */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          padding: '16px 26px 2px',
          flexWrap: 'wrap',
          fontFamily: MONO,
          fontSize: 11,
          letterSpacing: '.04em',
        }}
      >
        <SearchBox
          value={v.q}
          onChange={(e) => s.setPtQ(e.target.value)}
          placeholder="SEARCH PLAYTESTS…"
          w="260px"
          ifs={11}
        />
        {v.chips.map((c) => (
          <span key={c.key} onClick={() => s.togglePtChip(c.id)} style={chip(c.bd, c.clr, c.bg)}>
            {c.l}
          </span>
        ))}
        <span onClick={s.togglePtEnded} style={chip(v.endBd, v.endClr, v.endBg)}>
          {v.endLbl}
        </span>
        <div style={{ width: 1, height: 22, background: 'var(--gt-border-2)', margin: '0 4px', flex: 'none' }} />
        <FilterBar />
      </div>

      {/* grouped rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '14px 0 22px', flex: 1 }}>
        {v.groups.map((g) => (
          <div key={g.key} style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 26px', marginTop: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ fontFamily: PIXEL, fontSize: '7.5px', color: g.tc, transform: 'translateY(0.13em)' }}>
                {g.t}
              </div>
              <div style={{ fontFamily: MONO, fontSize: 10, color: 'var(--gt-muted)' }}>{g.n}</div>
              <div style={{ flex: 1, height: 1, background: 'var(--gt-line)' }} />
            </div>

            {g.rows.map((r) => {
              const wb = ptWinBorder(r.wLbl);
              return (
                <div
                  key={r.key}
                  onClick={() => s.openPtDet(r.id)}
                  className="d-hov-card"
                  style={{
                    display: 'flex',
                    gap: 16,
                    alignItems: 'stretch',
                    flexWrap: 'wrap',
                    border: '1px solid var(--gt-border)',
                    background: 'var(--gt-tile)',
                    padding: 14,
                    cursor: 'pointer',
                  }}
                >
                  {/* 1 — window box */}
                  <div
                    style={{
                      width: 108,
                      flex: 'none',
                      border: '1px ' + wb.bs + ' ' + wb.bd,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 7,
                      padding: '10px 6px',
                      textAlign: 'center',
                      boxSizing: 'border-box',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      {r.wLive ? (
                        <span
                          style={{
                            width: 7,
                            height: 7,
                            background: 'var(--acc)',
                            animation: 'gt-blink 1.1s steps(1) infinite',
                            flex: 'none',
                          }}
                        />
                      ) : null}
                      <span style={{ fontFamily: PIXEL, fontSize: '6.5px', color: r.wLblClr, transform: 'translateY(0.13em)' }}>
                        {r.wLbl}
                      </span>
                    </div>
                    <div style={{ fontFamily: PIXEL, fontSize: 10, color: 'var(--gt-text)' }}>{r.d1}</div>
                    {r.hasD2 ? (
                      <>
                        <div style={{ fontFamily: MONO, fontSize: 9, color: 'var(--gt-muted)', margin: '-3px 0' }}>↓</div>
                        <div style={{ fontFamily: PIXEL, fontSize: 10, color: 'var(--gt-text)' }}>{r.d2}</div>
                      </>
                    ) : null}
                    <div
                      style={{
                        fontFamily: MONO,
                        fontSize: 8,
                        letterSpacing: '.04em',
                        lineHeight: 1.5,
                        color: 'var(--gt-muted)',
                      }}
                    >
                      {r.wFoot}
                    </div>
                  </div>

                  {/* 2 — art */}
                  <div style={{ width: 190, flex: 'none', alignSelf: 'center' }}>
                    <div style={{ position: 'relative', aspectRatio: '460/215', background: r.fall, overflow: 'hidden' }}>
                      <PtCover style={{ position: 'absolute', inset: 0 }} fs={13} id={r.id} ini={r.ini} cands={r.cands} />
                    </div>
                  </div>

                  {/* 3 — body */}
                  <div
                    style={{
                      flex: 1,
                      minWidth: 260,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                      justifyContent: 'center',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--gt-text)' }}>{r.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--gt-text-5)' }}>{r.dev}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {r.chips.map((ch) => (
                        <span
                          key={ch.key}
                          style={{
                            fontFamily: MONO,
                            fontSize: '8.5px',
                            letterSpacing: '.06em',
                            border: '1px solid ' + ch.bd,
                            color: ch.c,
                            background: ch.bg,
                            padding: '3px 6px',
                          }}
                        >
                          {ch.t}
                        </span>
                      ))}
                    </div>
                    <div style={{ fontSize: '11.5px', lineHeight: 1.5, color: 'var(--gt-text-5)', maxWidth: 560 }}>
                      {r.note}
                    </div>
                    <div style={{ display: 'flex', gap: 28, marginTop: 2, flexWrap: 'wrap' }}>
                      {r.strips.map((st) => (
                        <div key={st.key} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 5,
                              fontFamily: MONO,
                              fontSize: '8.5px',
                              letterSpacing: '.08em',
                              color: st.lc,
                            }}
                          >
                            {st.tri ? <PixelTriangle w={7} h={6} /> : null}
                            {st.l}
                          </div>
                          <div style={{ display: 'flex', gap: 3, alignItems: 'center', minHeight: 18 }}>
                            {st.avs.map((f) => (
                              <Avatar
                                key={f.key}
                                style={{ flex: 'none' }}
                                size={18}
                                fs={6}
                                ini={f.i}
                                bg={f.bg}
                                hasAv={f.hasAv}
                                avBg={f.avBg}
                                title={f.name}
                              />
                            ))}
                            {st.none ? (
                              <span style={{ fontFamily: MONO, fontSize: 10, color: 'var(--gt-ghost)' }}>—</span>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 4 — actions */}
                  <div
                    style={{
                      width: 220,
                      flex: 'none',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 7,
                      justifyContent: 'center',
                    }}
                  >
                    <div
                      onClick={(ev) => {
                        ev.stopPropagation();
                        if (r.pUrl) window.open(r.pUrl, '_blank');
                        // The tab and the question go up together: this click is
                        // the only evidence a request was even attempted, and it
                        // is not evidence that one was SENT (selectors.3e#ptAskVM).
                        if (r.pAsk) s.openPtAsk(r.id);
                      }}
                      style={{
                        textAlign: 'center',
                        border: '1px solid ' + r.pBd,
                        background: r.pBg,
                        color: r.pClr,
                        fontFamily: MONO,
                        fontSize: '10.5px',
                        letterSpacing: '.03em',
                        padding: 10,
                        cursor: 'pointer',
                      }}
                    >
                      {r.pTxt}
                    </div>
                    <div
                      onClick={(ev) => {
                        ev.stopPropagation();
                        void s.ptToggleInt(r.id);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        border: '1px solid ' + r.tBd,
                        background: r.tBg,
                        color: r.tClr,
                        fontFamily: MONO,
                        fontSize: 10,
                        padding: 8,
                        cursor: 'pointer',
                      }}
                    >
                      <PixelTriangle w={8} h={7} />
                      {r.tTxt}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        {v.hasEndedNote ? (
          <div
            onClick={s.togglePtEnded}
            style={{
              margin: '6px 26px 0',
              border: '1px dashed var(--gt-border)',
              padding: '11px 14px',
              fontFamily: MONO,
              fontSize: 10,
              letterSpacing: '.03em',
              color: 'var(--gt-muted)',
              cursor: 'pointer',
            }}
          >
            {v.endedNote}
          </div>
        ) : null}

        {v.empty ? (
          <div
            style={{
              padding: '60px 26px',
              textAlign: 'center',
              fontFamily: MONO,
              fontSize: 12,
              color: 'var(--gt-muted)',
              lineHeight: 2,
            }}
          >
            {PT_EMPTY_DESKTOP}
          </div>
        ) : null}
      </div>

      <div
        style={{
          borderTop: '1px solid var(--gt-line)',
          padding: '12px 26px',
          fontFamily: MONO,
          fontSize: '9.5px',
          lineHeight: 1.8,
          color: 'var(--gt-muted-2)',
        }}
      >
        {PT_DATA_FOOTER_DESKTOP}
      </div>
    </div>
  );
}
